// ===================================================
// GAM Command Center — Embed Block Extension
// iframe embed for YouTube, Google Maps, any URL
// Auto-detects YouTube/Vimeo for proper embed URLs
// ===================================================

import { Node, mergeAttributes } from '@tiptap/core';
import { parseEmbedUrl } from './embed-url';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embedBlock: {
      setEmbed: (attrs: { url: string }) => ReturnType;
    };
  }
}


export const EmbedBlock = Node.create({
  name: 'embedBlock',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      url: { default: '' },
      embedUrl: { default: '' },
      embedType: { default: 'generic' },
      aspectRatio: { default: '16/9' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="embed"]',
        getAttrs: (el) => ({
          url: (el as HTMLElement).getAttribute('data-url') || '',
          embedUrl: (el as HTMLElement).getAttribute('data-embed-url') || '',
          embedType: (el as HTMLElement).getAttribute('data-embed-type') || 'generic',
        }),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { url, embedUrl, embedType, aspectRatio } = node.attrs;
    const parsed = parseEmbedUrl(url);
    const finalEmbedUrl = embedUrl || parsed.embedUrl;

    return [
      'div',
      mergeAttributes(
        {
          'data-type': 'embed',
          'data-url': url,
          'data-embed-url': finalEmbedUrl,
          'data-embed-type': embedType || parsed.embedType,
          class: 'gam-embed',
        },
        HTMLAttributes
      ),
      [
        'div',
        { class: 'gam-embed__wrapper', style: `aspect-ratio: ${aspectRatio}` },
        [
          'iframe',
          {
            src: finalEmbedUrl,
            frameborder: '0',
            allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
            allowfullscreen: 'true',
            loading: 'lazy',
            class: 'gam-embed__iframe',
          },
        ],
      ],
    ];
  },

  addCommands() {
    return {
      setEmbed:
        ({ url }) =>
        ({ commands }) => {
          const { embedUrl, embedType } = parseEmbedUrl(url);
          return commands.insertContent({
            type: this.name,
            attrs: { url, embedUrl, embedType },
          });
        },
    };
  },
});
